/**
 * Listado de cotizaciones x mayor — pestaña "X mayor" del módulo.
 *
 * Mismo comportamiento que el listado al detalle: filtros por fechas,
 * empresa y texto; anuladas solo si se piden; "Vencida" derivada de
 * fechavence. Acciones: reimprimir y anular (flag `borrar`).
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { FP, fpInputSx, fpDataGridSx, StatusPill, ConfirmarDialog } from "components/financepro";
import { useApi } from "lib/api";
import { useMultiempresa, SelectorEmpresa } from "lib/multiempresa";
import {
  imprimirCotizacionXmayor,
  monedaLabel,
} from "modules/cotizaciones/impresioncotizacionxmayor";

const inicioMesISO = () => `${new Date().toISOString().slice(0, 8)}01`;
const hoyISO = () => new Date().toISOString().slice(0, 10);

const parseN = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const fmtMoney = (v) =>
  parseN(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        No hay cotizaciones x mayor en el rango seleccionado
      </Typography>
    </Stack>
  );
}

function estadoCotizacion(row) {
  if ((row.status || "A") === "X") return { label: "Anulada", variant: "danger" };
  const vence = String(row.fechavence_iso || "").slice(0, 10);
  if (vence && vence < hoyISO()) return { label: "Vencida", variant: "warning" };
  return { label: "Activa", variant: "success" };
}

export default function ListadoCotizacionesXmayor({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const puedeAnular = puedeAccionPrograma(controller.roleDetalles, "cotizaciones", "borrar");
  const { activo: multiActivo, empresas } = useMultiempresa();

  const [desde, setDesde] = useState(inicioMesISO());
  const [hasta, setHasta] = useState(hoyISO());
  const [empresa, setEmpresa] = useState("0");
  const [texto, setTexto] = useState("");
  const [conAnuladas, setConAnuladas] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { ok, data } = await post("/api/cotizacionxmayor_listar", {
        desde,
        hasta,
        empresa: empresa === "0" ? "" : empresa,
        texto,
        incluir_anuladas: conAnuladas ? "S" : "N",
      });
      if (ok && Array.isArray(data.cotizaciones)) setRows(data.cotizaciones);
      else setError(data.Error || "No se pudieron cargar las cotizaciones");
    } catch {
      setError("Error de red o servidor");
    } finally {
      setLoading(false);
    }
  }, [post, desde, hasta, empresa, texto, conAnuladas]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Imprimir ----
  const imprimir = useCallback(
    async (id) => {
      const { ok, data } = await post("/api/cotizacionxmayor_detalle", { id });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo leer la cotización", color: "error" });
        return;
      }
      imprimirCotizacionXmayor(data);
    },
    [post, showAlert]
  );

  // ---- Anular ----
  const [anular, setAnular] = useState(null);
  const [anulando, setAnulando] = useState(false);

  const confirmarAnular = async () => {
    if (!anular) return;
    setAnulando(true);
    try {
      const { ok, data } = await post("/api/cotizacionxmayor_anular", { id: anular.id });
      if (ok) {
        showAlert({ message: data.mensajeok || "Cotización anulada", color: "success" });
        setAnular(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo anular", color: "error" });
      }
    } finally {
      setAnulando(false);
    }
  };

  const columnas = useMemo(
    () => [
      { field: "id", headerName: "#", width: 70 },
      { field: "fecha", headerName: "Fecha", width: 100 },
      { field: "fechavence", headerName: "Vence", width: 100 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 190 },
      {
        field: "unidades",
        headerName: "Unidades",
        width: 95,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "total",
        headerName: "Total",
        width: 140,
        align: "right",
        headerAlign: "right",
        valueGetter: (_v, row) => `${monedaLabel(row.moneda)} ${fmtMoney(row.total)}`,
      },
      { field: "tipofactura", headerName: "Tipo", width: 100 },
      {
        field: "estado",
        headerName: "Estado",
        width: 110,
        sortable: false,
        renderCell: (params) => {
          const e = estadoCotizacion(params.row);
          return <StatusPill label={e.label} variant={e.variant} />;
        },
      },
      {
        field: "acciones",
        headerName: "",
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Imprimir">
              <IconButton
                size="small"
                onClick={() => imprimir(params.row.id)}
                sx={{ color: FP.color.navy }}
              >
                <PrintOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {puedeAnular && (params.row.status || "A") === "A" && (
              <Tooltip title="Anular">
                <IconButton
                  size="small"
                  onClick={() => setAnular(params.row)}
                  sx={{ color: FP.color.danger }}
                >
                  <BlockOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [imprimir, puedeAnular]
  );

  return (
    <Box
      component={embedded ? "section" : "main"}
      sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            {multiActivo && (
              <Grid item xs={6} sm={3} md={2}>
                <SelectorEmpresa
                  value={empresa}
                  onChange={setEmpresa}
                  empresas={empresas}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Buscar"
                placeholder="Cliente, RNC, número…"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && cargar()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={conAnuladas}
                    onChange={(e) => setConAnuladas(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                    Incluir anuladas
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          {error ? (
            <Box sx={{ mb: 1.6 }}>
              <Typography sx={{ color: FP.color.danger, fontWeight: 700, fontSize: "0.9rem" }}>
                {error}
              </Typography>
              <Button onClick={cargar} sx={{ textTransform: "none", fontWeight: 700, px: 0 }}>
                Reintentar
              </Button>
            </Box>
          ) : null}

          <Typography
            sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem", mb: 1.6 }}
          >
            {rows.length} cotizaciones x mayor
          </Typography>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columnas}
              getRowId={(r) => r.id}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      <ConfirmarDialog
        abierto={!!anular}
        titulo={`Anular cotización x mayor #${anular?.id || ""}`}
        destructivo
        mensaje="La cotización quedará anulada y solo será visible marcando «Incluir anuladas». ¿Desea continuar?"
        textoConfirmar="Anular"
        cargando={anulando}
        onCerrar={() => setAnular(null)}
        onConfirmar={confirmarAnular}
      />
    </Box>
  );
}
